import { useEffect, useRef, useState } from "react"
import Navbar from "@/components/Navbar"
import Footer from "@/components/Footer"
import AppealFileUpload, { type AppealAttachment } from "@/components/AppealFileUpload"
import AppealStatusLookup from "@/components/AppealStatusLookup"
import {ShieldCheck, Send, CheckCircle2, ChevronDown, Loader2, AlertCircle, Info} from 'lucide-react'

const LOGO_URL = "https://static.lumi.new/4c/4c8f124f3e19139e5d5f616a0dcd86e1.png"
const PROJECT_ID = "p427286551862546432"
const FUNCTION_URL = `https://api.lumi.new/v1/functions/${PROJECT_ID}/parking-appeals`

const REASON_OPTIONS = [
  "Valid parking permit was displayed",
  "Parking payment was made",
  "Registered through visitor parking",
  "Vehicle information was entered incorrectly",
  "Signage was unclear or unavailable",
  "Medical or emergency circumstances",
  "Loading / delivery / contractor activity",
  "Accessible parking issue",
  "Notice issued in error",
  "Vehicle was not at this location",
  "Other",
]

const REGION_OPTIONS = [
  "British Columbia", "Alberta", "Ontario", "Quebec", "Manitoba", "Saskatchewan",
  "Nova Scotia", "New Brunswick", "Newfoundland and Labrador", "Prince Edward Island",
  "Washington", "California", "Oregon", "Texas", "New York", "Florida", "Illinois",
]

const SECTIONS = [
  { id: "contact", label: "Contact" },
  { id: "notice", label: "Notice" },
  { id: "reason", label: "Reason" },
  { id: "evidence", label: "Evidence" },
  { id: "declaration", label: "Submit" },
]

interface AppealFormData {
  firstName: string
  lastName: string
  email: string
  phone: string
  preferredContactMethod: "Email" | "Phone"
  noticeNumber: string
  licensePlate: string
  plateRegion: string
  propertyLocation: string
  noticeDate: string
  noticeTime: string
  reasonCategory: string
  description: string
  stallNumber: string
  permitNumber: string
  paymentReference: string
  parkingApp: string
  propertyUnit: string
  tenantBusiness: string
}

const initialFormData: AppealFormData = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  preferredContactMethod: "Email",
  noticeNumber: "",
  licensePlate: "",
  plateRegion: "",
  propertyLocation: "",
  noticeDate: "",
  noticeTime: "",
  reasonCategory: "",
  description: "",
  stallNumber: "",
  permitNumber: "",
  paymentReference: "",
  parkingApp: "",
  propertyUnit: "",
  tenantBusiness: "",
}

const ADDITIONAL_FIELDS: { name: keyof AppealFormData, label: string }[] = [
  { name: "stallNumber", label: "Parking Stall Number" },
  { name: "permitNumber", label: "Permit Number" },
  { name: "paymentReference", label: "Payment Transaction / Reference Number" },
  { name: "parkingApp", label: "Parking App Used" },
  { name: "propertyUnit", label: "Property / Unit Being Visited" },
  { name: "tenantBusiness", label: "Business or Tenant Being Visited" },
]

function Appeals() {
  const [formData, setFormData] = useState<AppealFormData>(initialFormData)
  const [attachments, setAttachments] = useState<AppealAttachment[]>([])
  const [showAdditional, setShowAdditional] = useState(false)
  const [consentAccurate, setConsentAccurate] = useState(false)
  const [consentUnderstanding, setConsentUnderstanding] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [appealReference, setAppealReference] = useState("")
  const [activeSection, setActiveSection] = useState("contact")

  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({})

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        })
      },
      { rootMargin: "-30% 0px -55% 0px" }
    )

    SECTIONS.forEach((section) => {
      const el = sectionRefs.current[section.id]
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [])

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!consentAccurate || !consentUnderstanding) return

    setSubmitting(true)
    setSubmitError("")

    try {
      const payload = {
        action: "submit",
        ...formData,
        attachments: attachments
          .filter((item) => item.fileUrl)
          .map((item) => ({ fileName: item.fileName, fileUrl: item.fileUrl })),
        consentAccurate,
        consentUnderstanding,
      }

      const response = await fetch(FUNCTION_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })

      const data = await response.json()

      if (!response.ok || !data?.appealReference) {
        throw new Error(data?.error || "Submission failed")
      }

      setAppealReference(data.appealReference)
    } catch (error) {
      console.error("Appeal submission failed:", error)
      setSubmitError("We were unable to submit your appeal right now. Please try again in a few minutes.")
    } finally {
      setSubmitting(false)
    }
  }

  const activeIndex = Math.max(0, SECTIONS.findIndex((section) => section.id === activeSection))

  if (appealReference) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-36 sm:pt-44 pb-24 px-5 sm:px-8">
          <div className="mx-auto max-w-xl text-center">
            <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Appeal Submitted</h1>
            <p className="mt-3 text-slate-600">Thank you. Your appeal has been received by Sentrio.</p>

            <div className="mt-8 rounded-2xl border border-slate-200 bg-slate-50 p-6">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Appeal Reference</p>
              <p className="mt-1 text-2xl font-bold text-sky-600">{appealReference}</p>
            </div>

            <p className="mt-6 text-sm text-slate-500">
              Please save this reference number for your records. A confirmation email has been sent to the
              address on your appeal. If you do not see it, check your spam or junk folder. Our team will review
              the information provided and contact you if additional details are required.
            </p>

            <a
              href="/"
              className="mt-8 inline-flex items-center justify-center rounded-lg bg-slate-900 px-8 py-3 font-semibold text-white transition-all hover:bg-slate-700"
            >
              Return to Homepage
            </a>
          </div>
        </section>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Mobile progress indicator */}
      <div className="lg:hidden fixed top-20 left-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="px-5 py-2.5">
          <div className="flex items-center justify-between text-[11px] font-semibold text-slate-400">
            <span>Step {activeIndex + 1} of {SECTIONS.length}</span>
            <span className="text-slate-600">{SECTIONS[activeIndex]?.label}</span>
          </div>
          <div className="mt-1.5 h-1 w-full rounded-full bg-slate-100">
            <div
              className="h-1 rounded-full bg-sky-500 transition-all duration-300"
              style={{ width: `${((activeIndex + 1) / SECTIONS.length) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Header */}
      <section className="pt-36 sm:pt-44 lg:pt-40 pb-12 sm:pb-16 px-5 sm:px-8">
        <div className="mx-auto max-w-3xl">
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5">
            <img src={LOGO_URL} alt="Sentrio Security" className="h-4 w-4 rounded-full object-cover" />
            <span className="text-xs font-semibold tracking-wide text-slate-500">Secure Appeals Portal | Sentrio</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 leading-tight">
            Appeal a Parking Notice
          </h1>
          <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
            If you believe a parking notice was issued incorrectly or there were circumstances that should be
            considered, you may submit an appeal below. Please provide complete and accurate information to help
            our team review your request.
          </p>

          <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <Info className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-600" />
            <p className="text-sm text-amber-800">
              Submitting an appeal does not automatically cancel or reduce a parking notice. Each appeal is
              reviewed based on the information and supporting documentation provided.
            </p>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="px-5 sm:px-8 pb-24">
        <form onSubmit={handleSubmit} className="mx-auto max-w-3xl space-y-10">
          {/* 1. Contact Information */}
          <div
            id="contact"
            ref={(el) => { sectionRefs.current.contact = el }}
            className="rounded-2xl border border-slate-200 p-6 sm:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900">1. Contact Information</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">First Name *</label>
                <input
                  type="text" name="firstName" required value={formData.firstName} onChange={handleChange}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Last Name *</label>
                <input
                  type="text" name="lastName" required value={formData.lastName} onChange={handleChange}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Email Address *</label>
                <input
                  type="email" name="email" required value={formData.email} onChange={handleChange}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Phone Number</label>
                <input
                  type="tel" name="phone" value={formData.phone} onChange={handleChange}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Preferred Contact Method</label>
                <div className="flex gap-3">
                  {(["Email", "Phone"] as const).map((method) => (
                    <button
                      key={method}
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, preferredContactMethod: method }))}
                      className={`rounded-lg px-5 py-2.5 text-sm font-semibold transition-all border ${
                        formData.preferredContactMethod === method
                          ? "border-sky-500 bg-sky-50 text-sky-700"
                          : "border-slate-300 text-slate-600 hover:border-slate-400"
                      }`}
                    >
                      {method}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 2. Parking Notice Information */}
          <div
            id="notice"
            ref={(el) => { sectionRefs.current.notice = el }}
            className="rounded-2xl border border-slate-200 p-6 sm:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900">2. Parking Notice Information</h2>
            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Notice / Citation Number *</label>
                <input
                  type="text" name="noticeNumber" required value={formData.noticeNumber} onChange={handleChange}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
                />
                <p className="mt-1.5 text-xs text-slate-500">You can find the notice number near the top of your parking notice.</p>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Vehicle Licence Plate *</label>
                <input
                  type="text" name="licensePlate" required value={formData.licensePlate} onChange={handleChange}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Province / State of Plate *</label>
                <input
                  type="text" name="plateRegion" required list="region-options" value={formData.plateRegion} onChange={handleChange}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
                  placeholder="e.g., British Columbia"
                />
                <datalist id="region-options">
                  {REGION_OPTIONS.map((region) => <option key={region} value={region} />)}
                </datalist>
              </div>
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Parking Location / Property *</label>
                <input
                  type="text" name="propertyLocation" required value={formData.propertyLocation} onChange={handleChange}
                  placeholder="Property name, address, or lot"
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Date of Notice</label>
                <input
                  type="date" name="noticeDate" value={formData.noticeDate} onChange={handleChange}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-semibold text-slate-700">Approximate Time of Notice</label>
                <input
                  type="time" name="noticeTime" value={formData.noticeTime} onChange={handleChange}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
                />
              </div>
            </div>
          </div>

          {/* 3. Reason for Appeal */}
          <div
            id="reason"
            ref={(el) => { sectionRefs.current.reason = el }}
            className="rounded-2xl border border-slate-200 p-6 sm:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900">3. Reason for Appeal</h2>
            <div className="mt-6">
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                Select the reason that best describes your appeal *
              </label>
              <select
                name="reasonCategory" required value={formData.reasonCategory} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
              >
                <option value="">Select a reason</option>
                {REASON_OPTIONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
              </select>
            </div>

            <div className="mt-5">
              <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                Tell us why you are appealing this notice *
              </label>
              <textarea
                name="description" required rows={6} maxLength={2000}
                value={formData.description} onChange={handleChange}
                className="w-full rounded-lg border border-slate-300 px-4 py-3 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40 resize-none"
                placeholder="Please explain the circumstances of your appeal..."
              />
              <div className="mt-1.5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 text-xs text-slate-500">
                <span>
                  Please provide as much relevant information as possible, including times, payment details,
                  permit information, or other circumstances that may assist with the review.
                </span>
                <span className="flex-shrink-0 sm:ml-3">{formData.description.length}/2000</span>
              </div>
            </div>
          </div>

          {/* 4. Supporting Evidence */}
          <div
            id="evidence"
            ref={(el) => { sectionRefs.current.evidence = el }}
            className="rounded-2xl border border-slate-200 p-6 sm:p-8"
          >
            <h2 className="text-lg font-bold text-slate-900">4. Supporting Evidence</h2>
            <p className="mt-1 mb-5 text-sm text-slate-500">Upload Supporting Documents or Photos</p>
            <AppealFileUpload attachments={attachments} setAttachments={setAttachments} />
          </div>

          {/* 5. Additional Information + Declaration */}
          <div
            id="declaration"
            ref={(el) => { sectionRefs.current.declaration = el }}
            className="rounded-2xl border border-slate-200 p-6 sm:p-8"
          >
            <button
              type="button"
              onClick={() => setShowAdditional(!showAdditional)}
              className="flex w-full items-center justify-between text-left"
            >
              <div>
                <h2 className="text-lg font-bold text-slate-900">5. Additional Information</h2>
                <p className="mt-1 text-sm text-slate-500">Optional details that may help with your review</p>
              </div>
              <ChevronDown className={`h-5 w-5 flex-shrink-0 text-slate-400 transition-transform ${showAdditional ? "rotate-180" : ""}`} />
            </button>

            {showAdditional ? (
              <div className="mt-6 grid sm:grid-cols-2 gap-5">
                {ADDITIONAL_FIELDS.map((field) => (
                  <div key={field.name}>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700">{field.label}</label>
                    <input
                      type="text"
                      name={field.name}
                      value={formData[field.name]}
                      onChange={handleChange}
                      className="w-full rounded-lg border border-slate-300 px-4 py-2.5 text-slate-900 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500/40"
                    />
                  </div>
                ))}
              </div>
            ) : null}

            {/* Declaration */}
            <div className="mt-8 space-y-3 border-t border-slate-100 pt-6">
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <input
                  type="checkbox" checked={consentAccurate}
                  onChange={(e) => setConsentAccurate(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                I confirm that the information submitted in this appeal is accurate to the best of my knowledge.
              </label>
              <label className="flex items-start gap-3 text-sm text-slate-700">
                <input
                  type="checkbox" checked={consentUnderstanding}
                  onChange={(e) => setConsentUnderstanding(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                />
                I understand that submitting an appeal does not guarantee that the parking notice will be cancelled or reduced.
              </label>
              <p className="pt-2 text-xs leading-relaxed text-slate-400">
                By submitting this form, you consent to Sentrio Security collecting and using the information and
                documentation provided to review and administer your appeal, in accordance with our{" "}
                <a href="/contact" className="text-sky-600 hover:underline">Privacy Policy</a>.
              </p>
            </div>

            {submitError ? (
              <div className="mt-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                {submitError}
              </div>
            ) : null}

            <button
              type="submit"
              disabled={!consentAccurate || !consentUnderstanding || submitting}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 px-8 py-4 text-lg font-semibold text-white shadow-lg shadow-sky-500/20 transition-all hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              {submitting ? "Submitting..." : "Submit Appeal"}
            </button>
          </div>
        </form>
      </section>

      {/* Status Lookup */}
      <section className="px-5 sm:px-8 pb-24">
        <div className="mx-auto max-w-3xl">
          <AppealStatusLookup />
        </div>
      </section>

      {/* Trust footer note */}
      <section className="border-t border-slate-100 bg-slate-50 px-5 sm:px-8 py-10">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-sm">
            <ShieldCheck className="h-5 w-5 text-sky-600" />
          </div>
          <h3 className="font-bold text-slate-900">Questions about a parking notice?</h3>
          <p className="mt-1 text-sm text-slate-500">
            For questions regarding an existing appeal, please include your notice number and appeal reference
            number when contacting Sentrio.
          </p>
          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm">
            <a href="/appeals" className="text-slate-500 hover:text-sky-600">Appeals</a>
            <a href="/contact" className="text-slate-500 hover:text-sky-600">Privacy Policy</a>
            <a href="/contact" className="text-slate-500 hover:text-sky-600">Terms</a>
            <a href="/contact" className="text-slate-500 hover:text-sky-600">Contact</a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Appeals
